import {
  Node,
  mergeAttributes,
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";
import {
  AlertCircle,
  Archive,
  FileText,
  Globe,
  Loader2,
  PencilLine,
  Sparkles,
  Table2,
} from "lucide-react";
import type { MutableRefObject } from "react";
import { cn } from "@/lib/utils";
import type { AnalysisSectionSource } from "@/api/analysis-api";
import { displaySources } from "./section-sources";

/**
 * One section of an analysis document, as a node in a single editor.
 *
 * The alternative — one editor per section, stacked — is simpler to write but
 * fragments selection, and being able to select and copy across the whole memo
 * is most of what makes a document feel like a document.
 *
 * The node exists so regenerating a section stays a targeted write. Without a
 * node carrying `sectionId`, the document would be one blob of HTML and the only
 * way to replace a section would be to diff prose the user has since edited.
 *
 * `isolating` is on deliberately. It stops Backspace at the start of a section
 * from merging it into the one above, which would destroy the section identity
 * every write depends on. Selecting and copying across sections still works.
 */

export interface AnalysisSectionHandlers {
  onRegenerate?: (sectionId: string) => void;
  /** True while any section is generating — regenerate is disabled throughout. */
  busy?: boolean;
}

export interface AnalysisSectionOptions {
  /**
   * A ref, not a plain callback: the editor is built once, but the handlers
   * close over state that changes on every render.
   */
  handlers?: MutableRefObject<AnalysisSectionHandlers>;
}

const SectionView = ({ node, extension }: NodeViewProps) => {
  const sectionId = String(node.attrs.sectionId ?? "");
  const title = String(node.attrs.title ?? "Section");
  const status = String(node.attrs.status ?? "done");
  const orphaned = Boolean(node.attrs.orphaned);
  const edited = Boolean(node.attrs.edited);
  const error = node.attrs.error ? String(node.attrs.error) : "";

  const handlers = (extension.options as AnalysisSectionOptions).handlers?.current;
  const running = status === "running";
  const canRegenerate = !orphaned && !handlers?.busy;

  return (
    <NodeViewWrapper
      as="section"
      data-section-id={sectionId}
      className="group/section relative mb-8 scroll-mt-24"
    >
      {/*
        Chrome is not part of the document. contentEditable={false} keeps the
        cursor out of it, so a stray keystroke cannot rename a section or leave
        the title in the exported file.
      */}
      <header
        contentEditable={false}
        className="mb-2 flex items-center gap-2 border-b border-border pb-1.5"
      >
        <h2 className="flex-1 text-base font-semibold text-foreground">{title}</h2>

        {running && (
          <span className="flex items-center gap-1 text-xs text-primary">
            <Loader2 className="h-3 w-3 animate-spin" />
            Writing
          </span>
        )}

        {orphaned && (
          <span
            className="flex items-center gap-1 text-xs text-amber-600"
            title="This section was removed from the template. Its content is kept, but it is no longer regenerated."
          >
            <Archive className="h-3 w-3" />
            Removed from template
          </span>
        )}

        {edited && !running && (
          <span
            className="flex items-center gap-1 text-xs text-muted-foreground"
            title="Edited by hand. A full regenerate will skip this section unless you choose to overwrite it."
          >
            <PencilLine className="h-3 w-3" />
            Edited
          </span>
        )}

        {status === "error" && !running && (
          <span
            className="flex items-center gap-1 text-xs text-destructive"
            title={error || "Generation failed"}
          >
            <AlertCircle className="h-3 w-3" />
            Failed
          </span>
        )}

        {!running && (
          <button
            type="button"
            onClick={() => sectionId && handlers?.onRegenerate?.(sectionId)}
            disabled={!canRegenerate}
            title={
              orphaned
                ? "This section is no longer in the template"
                : `Regenerate "${title}"`
            }
            aria-label={`Regenerate ${title}`}
            className={cn(
              "flex items-center gap-1 rounded px-1.5 py-0.5 text-xs opacity-0 transition-opacity",
              "focus-visible:opacity-100 group-hover/section:opacity-100",
              canRegenerate
                ? "text-muted-foreground hover:bg-muted hover:text-primary"
                : "cursor-not-allowed text-muted-foreground/50"
            )}
          >
            <Sparkles className="h-3 w-3" />
            Regenerate
          </button>
        )}
      </header>

      <NodeViewContent
        className={cn(
          "prose prose-sm max-w-none focus:outline-none",
          running && "opacity-60"
        )}
      />

      <SectionSources sources={node.attrs.sources} running={running} />
    </NodeViewWrapper>
  );
};

const SOURCE_ICON = {
  file: FileText,
  web: Globe,
  record: Table2,
} as const;

/**
 * Where this section's claims came from.
 *
 * `contentEditable={false}`, like the header — a citation is not part of the
 * document the author is writing, and the cursor must not be able to land in
 * it. Combined with the sources living on the node rather than in the prose,
 * that is what makes them survive editing: there is nothing here to delete.
 */
const SectionSources = ({
  sources,
  running,
}: {
  sources: unknown;
  running: boolean;
}) => {
  const resolved = displaySources(sources as AnalysisSectionSource[] | undefined);
  // Nothing while writing: sources arrive with the finished section, and an
  // empty "Sources" heading mid-generation reads as "found nothing".
  if (running || resolved.length === 0) return null;

  return (
    <footer contentEditable={false} className="mt-3 border-t border-border/60 pt-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/50">
          Sources
        </span>
        {resolved.map((source) => {
          const Icon = SOURCE_ICON[source.kind];
          const body = (
            <>
              <Icon className="h-3 w-3 shrink-0" />
              <span className="truncate">{source.label}</span>
            </>
          );

          return source.href ? (
            <a
              key={`${source.kind}-${source.label}`}
              href={source.href}
              target="_blank"
              rel="noreferrer"
              title={source.label}
              className="flex max-w-[16rem] items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              {body}
            </a>
          ) : (
            <span
              key={`${source.kind}-${source.label}`}
              title={source.label}
              className="flex max-w-[16rem] items-center gap-1 text-xs text-muted-foreground"
            >
              {body}
            </span>
          );
        })}
      </div>
    </footer>
  );
};

export const AnalysisSection = Node.create<AnalysisSectionOptions>({
  name: "analysisSection",
  group: "block",
  content: "block+",
  defining: true,
  isolating: true,

  addOptions() {
    return { handlers: undefined };
  },

  addAttributes() {
    return {
      sectionId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-section-id"),
        renderHTML: (attributes) =>
          attributes.sectionId
            ? { "data-section-id": attributes.sectionId }
            : {},
      },
      title: {
        default: "Section",
        parseHTML: (element) => element.getAttribute("data-title"),
        renderHTML: (attributes) =>
          attributes.title ? { "data-title": attributes.title } : {},
      },
      // Presentation only — the server is the source of truth for all of these,
      // so they are not written back out to HTML.
      status: { default: "done", renderHTML: () => ({}) },
      orphaned: { default: false, renderHTML: () => ({}) },
      edited: { default: false, renderHTML: () => ({}) },
      error: { default: "", renderHTML: () => ({}) },
      // Citations live here rather than as markers in the prose. Being an
      // attribute of the node instead of content inside it is what makes them
      // survive editing: there is nothing in the paragraph to delete.
      sources: { default: [], renderHTML: () => ({}) },
    };
  },

  parseHTML() {
    return [{ tag: "section[data-section-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["section", mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SectionView);
  },
});
